
import { container } from '@infrastructure/container';
import { PurchaseOrderItem, PurchaseOrderItemStatus } from '@domain/purchasing';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PurchaseOrderFilterCriteria } from '@features/purchasing/hooks/usePurchaseOrderFilter';

type Props = {
  id?: string;
  groupedBy?: 'supplier' | 'purchaseOrder' | 'product' | 'shipment';
  filters?: PurchaseOrderFilterCriteria;
};

export const usePurchaseOrderItems = ({ id, filters }: Props = {}) => {
  const queryClient = useQueryClient();


  // Get single item
  const { data: purchaseOrderItem, isLoading: isLoadingItem, error: errorItem } = useQuery({
    queryKey: ['purchaseOrderItem', id],
    queryFn: async () => {
      const result = await container.purchasingRepository.getPurchaseOrderItemById(id!);
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    enabled: !!id,
  });

  // Get all items
  const { data: purchaseOrderItems, isLoading: isLoadingItems, error } = useQuery({
    queryKey: ['purchaseOrderItems', filters],
    queryFn: () => container.purchasingRepository.getPurchaseOrderItems(filters),
    enabled: !id,
  });


  const { mutateAsync: updatePurchaseOrderItemStatus } = useMutation({
    mutationFn: async ({ itemId, status }: { itemId: string, status: PurchaseOrderItemStatus }): Promise<PurchaseOrderItem> => {
      const result = await container.purchasingRepository.updatePurchaseOrderItemStatus(itemId, status);
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchaseOrders'] });
      queryClient.invalidateQueries({ queryKey: ['purchaseOrder'] });
      queryClient.invalidateQueries({ queryKey: ['purchaseOrderItems'] });
    }
  });

  const { mutateAsync: deletePurchaseOrderItem } = useMutation({
    mutationFn: async ({ purchaseOrderId, itemId }: { purchaseOrderId: string, itemId: string }) => {
      const result = await container.purchasingRepository.deletePurchaseOrderItem(purchaseOrderId, itemId);
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchaseOrder'] });
      queryClient.invalidateQueries({ queryKey: ['purchaseOrders'] });
    }
  });

  const { mutateAsync: updatePurchaseOrderItem } = useMutation({
    mutationFn: async ({ itemId, quantity, productionStartDate, productionReadyDate, shipmentId, eta }: {
      itemId: string;
      quantity?: number;
      productionStartDate?: Date;
      productionReadyDate?: Date;
      shipmentId?: string;
      eta?: Date;
      orderId?: string
    }) => {
      const result = await container.purchasingRepository.updatePurchaseOrderItem(itemId, {
        quantity,
        productionStartDate,
        productionReadyDate,
        shipmentId,
        eta
      });
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    onSuccess: (_, { itemId, orderId }) => {
      queryClient.invalidateQueries({ queryKey: ['purchaseOrderItem', itemId] });
      queryClient.invalidateQueries({ queryKey: ['purchaseOrder', orderId] });
      queryClient.invalidateQueries({ queryKey: ['purchaseOrderItems'] });
    }
  });


  return {
    purchaseOrderItem,
    purchaseOrderItems,

    isLoadingItems,
    isLoadingItem,

    error: error || errorItem,
    updatePurchaseOrderItemStatus,
    deletePurchaseOrderItem,
    updatePurchaseOrderItem,
  };
};

